import { getDateTime, normalizeId, TODataGuid } from '@shared/utils';
import {
  BackendEntryType,
  CashFlowAggregates,
  CashFlowEntriesByDayMap,
  CashFlowEntry,
  CashFlowEntryStatus,
  CashFlowPaymentMethod,
  CashSession,
  FlowDirection,
  ReconciliationStatus,
  TenantDailyAggregatePoint,
  TenantDailySeriesPoint,
  TreasuryMoney,
} from '../entities/treasury.model';
import { DateTime } from 'luxon';

/**
 * Shape of a single row returned by `GET /OData/CashFlowEntries`.
 * Captures only the fields we read; extra columns are tolerated and ignored.
 */
export interface ODataCashFlowEntryRow {
  CashFlowEntryId: string;
  FinancialAccountId: string;
  CashSessionId?: string | null;
  OriginId?: string | null;
  OriginType?: string | number | null;
  Direction: string | number;
  EntryType: string | number;
  Amount: number | null;
  Description?: string | null;
  CompetenceDate: string;
  PaymentDate?: string | null;
  Status: string | number;
  Category?: string | null;
  PaymentMethod?: string | number | null;
  ReconciliationStatus?: string | number | null;
  MatchedBankTransactionId?: string | null;
  MatchConfidence?: number | string | null;
  MatchMethod?: string | null;
  ReconciledTransactionId?: string | null;
  AddedOnUtc?: string | null;
  Details?: string | null;
  CashAmount?: number | null;
  CardAmount?: number | null;
  PixAmount?: number | null;
  BankSlipAmount?: number | null;
  CheckAmount?: number | null;
  CashFlowEntryRequestId?: string | null;
  RequestDescription?: string | null;
  RequestDetails?: string | null;
  RequestAddedOnUtc?: string | null;
  TId?: string | null;
  CardholderName?: string | null;
  CardNumber?: string | null;
  CardBrand?: string | null;
  /** Customer linked to the originating request (sale/receipt). Resolved to FullName via `PersonLookupService`. */
  PersonId?: string | null;
  /** Audit PersonId of the user who recorded the entry. Resolved to FullName via `PersonLookupService`. */
  AddedBy?: string | null;
  /**
   * Link to the chart-of-accounts leaf node — used by the DRE Gerencial
   * inline drill-down (US7) to list the entries that make up a leaf row's
   * `Amount`. May be `null` on entries coming from the queue (MassTransit
   * consumers) that do not yet receive this binding.
   */
  ChartOfAccountsNodeId?: string | null;
}

const directionByName: Record<string, FlowDirection> = {
  In: FlowDirection.In,
  Out: FlowDirection.Out,
};

const backendEntryTypeByName: Record<string, BackendEntryType> = {
  Sale: BackendEntryType.Sale,
  Expense: BackendEntryType.Expense,
  Supply: BackendEntryType.Supply,
  Withdrawal: BackendEntryType.Withdrawal,
  Transfer: BackendEntryType.Transfer,
  Adjustment: BackendEntryType.Adjustment,
  // Tolerate the legacy/simplified string names that some payloads still
  // emit (chiefly `Entry`/`Exit` from the report path).
  Entry: BackendEntryType.Sale,
  Exit: BackendEntryType.Expense,
};

/**
 * Decodes `CashFlowEntries.EntryType` from either the string name (regular
 * OData GET, via Newtonsoft `StringEnumConverter`) or the raw int
 * (`$apply=groupby` aggregate path, which bypasses the converter) into
 * {@link BackendEntryType}. Falls back to {@link BackendEntryType.Sale} for
 * null/unknown values.
 */
export const decodeBackendEntryTypeFromOData = (
  value: string | number | null | undefined,
): BackendEntryType => {
  if (value === null || value === undefined) return BackendEntryType.Sale;
  if (typeof value === 'string') {
    return backendEntryTypeByName[value] ?? BackendEntryType.Sale;
  }
  if (
    value === BackendEntryType.Sale ||
    value === BackendEntryType.Expense ||
    value === BackendEntryType.Supply ||
    value === BackendEntryType.Withdrawal ||
    value === BackendEntryType.Transfer ||
    value === BackendEntryType.Adjustment
  ) {
    return value;
  }
  return BackendEntryType.Sale;
};

const statusByName: Record<string, CashFlowEntryStatus> = {
  Draft: CashFlowEntryStatus.Draft,
  Confirmed: CashFlowEntryStatus.Confirmed,
  Cancelled: CashFlowEntryStatus.Cancelled,
};

const paymentMethodByName: Record<string, CashFlowPaymentMethod> = {
  Cash: CashFlowPaymentMethod.Cash,
  Card: CashFlowPaymentMethod.Card,
  Pix: CashFlowPaymentMethod.Pix,
  BankSlip: CashFlowPaymentMethod.BankSlip,
  Check: CashFlowPaymentMethod.Check,
};

const reconciliationStatusByName: Record<string, ReconciliationStatus> = {
  Reconciled: ReconciliationStatus.Reconciled,
  Pending: ReconciliationStatus.Pending,
  Unmatched: ReconciliationStatus.Unmatched,
  Exception: ReconciliationStatus.Exception,
};

const decodeEnum = <T extends number>(
  value: string | number | null | undefined,
  byName: Record<string, T>,
  fallback: T,
): T => {
  if (typeof value === 'number') return value as T;
  if (typeof value === 'string' && value in byName) return byName[value];
  return fallback;
};

const decodeNullableEnum = <T extends number>(
  value: string | number | null | undefined,
  byName: Record<string, T>,
): T | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return value as T;
  if (value in byName) return byName[value];
  return null;
};


export const mapODataRowToCashFlowEntry = (
  row: ODataCashFlowEntryRow,
): CashFlowEntry => ({
  // `normalizeId` unwraps DX `Guid` instances (`{ _value }`) — declared in
  // `fieldTypes` on `cashFlowEntriesDataSource` — into primitive UUID strings.
  // Plain JSON callers (HTTP fetches, specs) pass strings through unchanged.
  CashFlowEntryId: normalizeId(row.CashFlowEntryId as never) ?? '',
  FinancialAccountId: normalizeId(row.FinancialAccountId as never) ?? '',
  CashSessionId: normalizeId(row.CashSessionId as never),
  Direction: decodeEnum(row.Direction, directionByName, FlowDirection.In),
  EntryType: decodeBackendEntryTypeFromOData(row.EntryType),
  Amount: (row.Amount ?? 0) as TreasuryMoney,
  Description: row.Description ?? '',
  CompetenceDate: row.CompetenceDate,
  PaymentDate: row.PaymentDate ?? null,
  Status: decodeEnum(row.Status, statusByName, CashFlowEntryStatus.Draft),
  Category: row.Category ?? null,
  PaymentMethod: decodeNullableEnum(row.PaymentMethod, paymentMethodByName),
  ReconciliationStatus: decodeNullableEnum(
    row.ReconciliationStatus,
    reconciliationStatusByName,
  ),
  MatchedBankTransactionId: normalizeId(row.MatchedBankTransactionId as never),
  MatchConfidence: row.MatchConfidence ?? null,
  MatchMethod: row.MatchMethod ?? null,
  originId: normalizeId(row.OriginId as never),
  originType: row.OriginType ?? null,
  reconciledTransactionId: normalizeId(row.ReconciledTransactionId as never),
  AddedOnUtc: row.AddedOnUtc ?? null,
  Details: row.Details ?? null,
  CashAmount: row.CashAmount ?? 0,
  CardAmount: row.CardAmount ?? 0,
  PixAmount: row.PixAmount ?? 0,
  BankSlipAmount: row.BankSlipAmount ?? 0,
  CheckAmount: row.CheckAmount ?? 0,
  CashFlowEntryRequestId: normalizeId(row.CashFlowEntryRequestId as never),
  RequestDescription: row.RequestDescription ?? null,
  TId: row.TId ?? null,
  CardholderName: row.CardholderName ?? null,
  CardNumber: row.CardNumber ?? null,
  CardBrand: row.CardBrand ?? null,
  PersonId: normalizeId(row.PersonId as never),
  AddedBy: normalizeId(row.AddedBy as never),
  ChartOfAccountsNodeId: normalizeId(row.ChartOfAccountsNodeId as never),
});

/**
 * Shape of one row returned by
 * `$apply=...groupby((Direction),aggregate(Amount with sum as Total))`, used
 * to drive `totalIn` / `totalOut` on the account-detail summary cards.
 *
 * Direction may be projected as a string ("In"/"Out") or as the numeric enum
 * (0/1) depending on the host's serialization.
 */
export interface ODataCashFlowDirectionAggregateRow {
  Direction: string | number;
  Total: number | null;
}

export interface ODataCashFlowDirectionAggregateResponse {
  value?: ODataCashFlowDirectionAggregateRow[];
}

/** Shape of `$apply=filter(...)/aggregate(Amount with sum as Total)`. */
export interface ODataCashFlowSumAggregateRow {
  Total: number | null;
}

export interface ODataCashFlowSumAggregateResponse {
  value?: ODataCashFlowSumAggregateRow[];
}

/**
 * Folds the groupby((Direction)) rows into `totalIn` / `totalOut` (absolute
 * values; the sign is implied by direction).
 */
export const mapCashFlowDirectionAggregateRows = (
  body:
    | ODataCashFlowDirectionAggregateResponse
    | ODataCashFlowDirectionAggregateRow[]
    | null
    | undefined,
): Pick<CashFlowAggregates, 'totalIn' | 'totalOut'> => {
  const rows = Array.isArray(body) ? body : body?.value ?? [];
  let totalIn = 0;
  let totalOut = 0;
  for (const row of rows) {
    const amount = Math.abs(row.Total ?? 0);
    const direction = decodeEnum(row.Direction, directionByName, FlowDirection.In);
    if (direction === FlowDirection.In) {
      totalIn += amount;
    } else if (direction === FlowDirection.Out) {
      totalOut += amount;
    }
  }
  return {
    totalIn: totalIn as TreasuryMoney,
    totalOut: totalOut as TreasuryMoney,
  };
};

/**
 * Reads the single-row sum returned by `$apply=filter(...)/aggregate(...)`
 * (or its bare-array variant). Returns 0 when the filter matches nothing.
 */
export const mapCashFlowSumAggregateRow = (
  body:
    | ODataCashFlowSumAggregateResponse
    | ODataCashFlowSumAggregateRow[]
    | null
    | undefined,
): number => {
  const rows = Array.isArray(body) ? body : body?.value ?? [];
  return Math.abs(rows[0]?.Total ?? 0);
};

/** Shape of one row returned by `$apply=...groupby((CompetenceDate,Direction,EntryType,FinancialAccountId),aggregate(...))`. */
export interface ODataDailySeriesRow {
  CompetenceDate: string;
  Direction: string | number;
  EntryType: string | number;
  FinancialAccountId: string;
  Total: number | null;
  Count: number | null;
}

export interface ODataDailySeriesResponse {
  value?: ODataDailySeriesRow[];
}

export const mapODataDailySeriesRow = (
  row: ODataDailySeriesRow,
): TenantDailySeriesPoint => ({
  CompetenceDate: row.CompetenceDate,
  Direction: decodeEnum(row.Direction, directionByName, FlowDirection.In),
  EntryType: decodeBackendEntryTypeFromOData(row.EntryType),
  // When the response goes through `DxOdataStoreService.fromOdata` with
  // `fieldTypes.FinancialAccountId = 'Guid'`, DX wraps the value in a
  // `{ _value }` envelope. `normalizeId` unwraps it (and tolerates plain
  // strings) so downstream Map keys / equality checks see a primitive uuid.
  FinancialAccountId: normalizeId(row.FinancialAccountId as never) ?? '',
  Total: row.Total ?? 0,
  Count: row.Count ?? 0,
});

/**
 * The aggregated `$apply` endpoint returns a bare array (no `{ value: [...] }`
 * envelope) on this backend, while non-aggregated OData calls still return the
 * standard wrapper. Accept both shapes so the mapper survives any future
 * normalization on the server side.
 */
export const mapODataDailySeriesResponse = (
  body: ODataDailySeriesResponse | ODataDailySeriesRow[] | null | undefined,
): TenantDailySeriesPoint[] => {
  const rows = Array.isArray(body) ? body : body?.value ?? [];
  return rows.map(mapODataDailySeriesRow);
};

/**
 * Shape of one row returned by
 * `$apply=filter(...)/groupby((Direction,EntryType,PaymentMethod,FinancialAccountId),aggregate(Amount with sum as Total,$count as Count))`.
 *
 * `PaymentMethod` is nullable on the server side; adjustments / system-generated
 * rows commonly omit it. The mapper preserves null so callers can decide whether
 * to skip the bucket (e.g. `dailyByPaymentMethod`) or sum it anyway (e.g.
 * `dailyTotals`).
 */
export interface ODataTenantDailyAggregateRow {
  Direction: string | number;
  EntryType: string | number;
  PaymentMethod?: string | number | null;
  FinancialAccountId: TODataGuid;
  Total: number | null;
  Count: number | null;
}

export interface ODataTenantDailyAggregateResponse {
  value?: ODataTenantDailyAggregateRow[];
}

export const mapODataTenantDailyAggregateRow = (
  row: ODataTenantDailyAggregateRow,
): TenantDailyAggregatePoint => ({
  Direction: decodeEnum(row.Direction, directionByName, FlowDirection.In),
  EntryType: decodeBackendEntryTypeFromOData(row.EntryType),
  PaymentMethod: decodeNullableEnum(row.PaymentMethod, paymentMethodByName),
  // See `mapODataDailySeriesRow` — when this goes through `fromOdata` with
  // a Guid `fieldType`, DX wraps the value in `{ _value }`; `normalizeId`
  // unwraps so the downstream `dailyTotalsByAccount` Map key is a plain uuid.
  FinancialAccountId: normalizeId(row.FinancialAccountId) ?? '',
  Total: row.Total ?? 0,
  Count: row.Count ?? 0,
});

/**
 * Tolerates both the bare-array and `{ value: [...] }` envelope variants,
 * matching the behavior of {@link mapODataDailySeriesResponse}.
 */
export const mapODataTenantDailyAggregateResponse = (
  body:
    | ODataTenantDailyAggregateResponse
    | ODataTenantDailyAggregateRow[]
    | null
    | undefined,
): TenantDailyAggregatePoint[] => {
  const rows = Array.isArray(body) ? body : body?.value ?? [];
  return rows.map(mapODataTenantDailyAggregateRow);
};

/**
 * Net-balance rows: one per `(Y,M,D,Direction)` from the **unfiltered** (account
 * + window only) `$apply`. Drives the running balance and the per-day `Count`,
 * both of which must reflect the real account state regardless of the grid's
 * header filters (so the banner's balance and count stay in lockstep).
 */
export interface ODataCashFlowDayAggregateRow {
  Y: number | null;
  M: number | null;
  D: number | null;
  Direction: string | number;
  Total: number | null;
  Count: number | null;
}

export interface ODataCashFlowDayAggregateResponse {
  value?: ODataCashFlowDayAggregateRow[];
}

/**
 * Separator-key rows: one per `(Y,M,D)` from the **filtered** (grid filter +
 * window) `$apply`. `MaxAddedOnUtc` is the day's last *visible* row — the value
 * the grid matches its row against, so the day banner always lands on a row the
 * grid actually renders.
 */
export interface ODataCashFlowDayKeyRow {
  Y: number | null;
  M: number | null;
  D: number | null;
  MaxAddedOnUtc: string | null;
}

export interface ODataCashFlowDayKeyResponse {
  value?: ODataCashFlowDayKeyRow[];
}

/** Local calendar day (`YYYY-MM-DD`) from a `(Y,M,D)` aggregate row, or null. */
const isoDateFromYMD = (row: {
  Y: number | null;
  M: number | null;
  D: number | null;
}): string | null => {
  if (row.Y == null || row.M == null || row.D == null) return null;
  const date = DateTime.fromObject({ year: row.Y, month: row.M, day: row.D });
  return date.isValid ? date.toISODate() : null;
};

export const mapCashFlowEntriesByDayRows = (
  netBody:
    | ODataCashFlowDayAggregateResponse
    | ODataCashFlowDayAggregateRow[]
    | null
    | undefined,
  keyBody:
    | ODataCashFlowDayKeyResponse
    | ODataCashFlowDayKeyRow[]
    | null
    | undefined,
  priorBalance = 0,
): CashFlowEntriesByDayMap => {
  const netRows = Array.isArray(netBody) ? netBody : netBody?.value ?? [];
  const keyRows = Array.isArray(keyBody) ? keyBody : keyBody?.value ?? [];

  // Per-day net + count (unfiltered): both the running balance and the banner's
  // movement count reflect the real account state, not the filtered subset the
  // grid is showing — keeping balance and count in lockstep.
  const daily = new Map<string, { net: number; count: number }>();
  for (const row of netRows) {
    const isoDate = isoDateFromYMD(row);
    if (!isoDate) continue;
    const amount = Math.abs(row.Total ?? 0);
    const direction = decodeEnum(row.Direction, directionByName, FlowDirection.In);
    const net = direction === FlowDirection.Out ? -amount : amount;
    const bucket = daily.get(isoDate) ?? { net: 0, count: 0 };
    bucket.net += net;
    bucket.count += row.Count ?? 0;
    daily.set(isoDate, bucket);
  }

  // Per-day separator key (filtered): keep the latest `MaxAddedOnUtc` (compared
  // as instants) — the day's last visible row, which the grid matches against.
  const maxAddedOnUtcByDate = new Map<string, string>();
  for (const row of keyRows) {
    const isoDate = isoDateFromYMD(row);
    if (!isoDate || !row.MaxAddedOnUtc) continue;
    const current = maxAddedOnUtcByDate.get(isoDate);
    if (
      !current ||
      (getDateTime(row.MaxAddedOnUtc)?.toMillis() || 0) >
        (getDateTime(current)?.toMillis() || 0)
    ) {
      maxAddedOnUtcByDate.set(isoDate, row.MaxAddedOnUtc);
    }
  }

  // Walk every day with account activity in chronological order so the running
  // balance carries forward across days the filter may hide, but emit an entry
  // only for days that have a visible row to anchor the banner to.
  const orderedDays = [...daily.entries()].sort((a, b) =>
    a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0,
  );
  const byMaxAddedOnUtc: CashFlowEntriesByDayMap = {};
  let runningBalance = priorBalance;
  for (const [date, { net, count }] of orderedDays) {
    runningBalance += net;
    const maxAddedOnUtc = maxAddedOnUtcByDate.get(date);
    if (!maxAddedOnUtc) continue;
    byMaxAddedOnUtc[maxAddedOnUtc] = {
      Day: date,
      totalAmount: runningBalance,
      Count: count,
    };
  }
  return byMaxAddedOnUtc;
};

/**
 * Coerces a single `CashSession` row coming through
 * `DxOdataStoreService.fromOdata` (with `fieldTypes` Guid declared) so all
 * UUID fields are primitive strings before reaching the store.
 *
 * DX wraps Edm.Guid response values in `{ _value }` envelopes — necessary
 * for outgoing filter serialization but a footgun for downstream code that
 * does `Map.get(session.FinancialAccountId)`, `=== personId` comparisons,
 * `router.navigate([..., session.CashSessionId])`, etc. The mapper
 * `normalizeId`s each Guid field at the boundary so the contract on
 * `CashSession.<id>: string` is honored end-to-end. Extra fields (Status,
 * dates, balances) pass through unchanged.
 */
export const mapODataRowToCashSession = (
  row: CashSession & {
    CashSessionId?: TODataGuid;
    FinancialAccountId?: TODataGuid;
    OpenedBy?: TODataGuid;
    ClosedBy?: TODataGuid;
  },
): CashSession => ({
  ...row,
  CashSessionId: normalizeId(row.CashSessionId) ?? '',
  FinancialAccountId: normalizeId(row.FinancialAccountId) ?? '',
  OpenedBy: normalizeId(row.OpenedBy) ?? '',
  ClosedBy: normalizeId(row.ClosedBy) ?? null,
});
